import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { combineLatest, map, switchMap } from 'rxjs';

import { AlertMessage } from 'app/models/alert.model';
import { SubscriptionAlert } from '../../models/subscription-alert.model';
import { OrderApiActions } from '../order/order.actions';
import {
  selectIsActiveSubscriptionAutoRenewCancelled,
  selectIsFamilyOptionAutoRenewCancelledOnly,
} from '../subscriptions/subscriptions.selectors';
import { SubscriptionAlertsActions } from './subscription-alerts.actions';
import { selectSubscriptionAlertsMaxId } from './subscription-alerts.selectors';

@Injectable()
export class SubscriptionAlertsEffects {
  constructor(private actions$: Actions, private store: Store) {}

  replacePermanentAlertWhenReinit$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SubscriptionAlertsActions.reinitPermanentAlert),
      concatLatestFrom(() => this.store.select(selectSubscriptionAlertsMaxId)),
      switchMap(([_, maxId]) =>
        combineLatest([
          this.store.select(selectIsActiveSubscriptionAutoRenewCancelled),
          this.store.select(selectIsFamilyOptionAutoRenewCancelledOnly),
        ]).pipe(
          map(([isActiveSubCancelled, isFamilyOptionCancelled]) => {
            if (!isActiveSubCancelled && !isFamilyOptionCancelled) {
              return SubscriptionAlertsActions.replacePermanentAlert({
                alert: null,
              });
            }
            const id: number = maxId + 1;
            const isPermanent: boolean = true;
            const message: AlertMessage = { portions: [] };
            if (isActiveSubCancelled) {
              message.portions = [
                {
                  text: 'dashboard.subscription.alerts.subscriptionRenewalSuspended',
                  action: null,
                },
                {
                  text: 'dashboard.subscription.alerts.resumeYourSubscription',
                  action: OrderApiActions.startResuming({
                    isFamilyOption: false,
                  }),
                },
                { text: '.', action: null },
              ];
            } else {
              message.portions = [
                {
                  text: 'dashboard.subscription.alerts.familyOptionRenewalSuspended',
                  action: null,
                },
                {
                  text: 'dashboard.subscription.alerts.restoreTheFamilyOption',
                  action: OrderApiActions.startResuming({
                    isFamilyOption: true,
                  }),
                },
                { text: '.', action: null },
              ];
            }
            return SubscriptionAlertsActions.replacePermanentAlert({
              alert: { id, isPermanent, message },
            });
          })
        )
      )
    );
  });

  generateIdAndAddAlertWhenCreatingIt$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SubscriptionAlertsActions.createAlert),
      concatLatestFrom(() => this.store.select(selectSubscriptionAlertsMaxId)),
      map(([action, maxId]) => {
        const id: number = maxId + 1;
        const { message, isPermanent } = action;
        const alert: SubscriptionAlert = { id, message, isPermanent };
        return SubscriptionAlertsActions.addAlert({ alert });
      })
    );
  });
}
